import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Dialog,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

/**
 * PropertyDiagramViewer
 *
 * Renders a small property/site-diagram thumbnail. Tapping it opens a
 * full-screen viewer with pan + zoom support (pinch on touch devices,
 * mouse wheel + drag on desktop).
 *
 * Designed to be shown on:
 *   - Admin job-detail pages (JobDetailView)
 *   - Crew-facing job-detail dialog (CrewToday)
 *
 * Props:
 *   - signedUrl   (string, required) Pre-signed S3 URL for the diagram.
 *   - filename    (string) Original filename (shown as caption).
 *   - thumbnailSize (number) px size of the square thumbnail. Default 96.
 *   - title       (string) Optional title shown in the fullscreen header.
 *   - compact     (bool) Render an even smaller inline pill thumbnail.
 */
const PropertyDiagramViewer = ({
  signedUrl,
  filename,
  thumbnailSize = 96,
  title,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  if (!signedUrl) return null;

  const headerLabel = title || t('jobs.propertyDiagram', 'Property Diagram');

  const openFull = () => setOpen(true);
  const closeFull = () => setOpen(false);

  return (
    <>
      <Paper
        elevation={1}
        onClick={openFull}
        role="button"
        tabIndex={0}
        aria-label={t('jobs.propertyDiagramOpenAria', 'Open property diagram full screen')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFull();
          }
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.5,
          p: compact ? 0.75 : 1,
          pr: compact ? 1.25 : 1.5,
          cursor: 'pointer',
          borderRadius: 2,
          transition: 'box-shadow 0.15s, transform 0.05s',
          '&:hover': { boxShadow: 3 },
          '&:active': { transform: 'scale(0.98)' },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: thumbnailSize,
            height: thumbnailSize,
            flexShrink: 0,
            borderRadius: 1.5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {!imgLoaded && !imgError && (
            <CircularProgress size={20} aria-label={t('common.loading', 'Loading')} />
          )}
          {imgError ? (
            <MapIcon color="disabled" />
          ) : (
            <Box
              component="img"
              src={signedUrl}
              alt={headerLabel}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: imgLoaded ? 'block' : 'none',
              }}
            />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MapIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
              {headerLabel}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {t('jobs.propertyDiagramTapHint', 'Tap to view full screen')}
          </Typography>
        </Box>
      </Paper>

      <Dialog
        open={open}
        onClose={closeFull}
        fullScreen
        aria-labelledby="property-diagram-fullscreen-title"
        PaperProps={{ sx: { bgcolor: '#000' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: 'common.white',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              id="property-diagram-fullscreen-title"
              variant="subtitle1"
              sx={{ fontWeight: 600 }}
              noWrap
            >
              {headerLabel}
            </Typography>
            {filename && (
              <Typography variant="caption" sx={{ opacity: 0.75 }} noWrap>
                {filename}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={closeFull}
            aria-label={t('common.close', 'Close')}
            sx={{ color: 'common.white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <TransformWrapper
          initialScale={1}
          minScale={0.5}
          maxScale={8}
          doubleClick={{ mode: 'toggle', step: 2 }}
          wheel={{ step: 0.15 }}
          pinch={{ step: 5 }}
          panning={{ velocityDisabled: false }}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <TransformComponent
                wrapperStyle={{
                  width: '100%',
                  height: '100%',
                  background: '#000',
                }}
                contentStyle={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={signedUrl}
                  alt={headerLabel}
                  draggable={false}
                  sx={{
                    maxWidth: '100vw',
                    maxHeight: '100vh',
                    objectFit: 'contain',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    pointerEvents: 'none',
                  }}
                />
              </TransformComponent>

              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                  display: 'flex',
                  gap: 1,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  borderRadius: 999,
                  px: 1,
                  py: 0.5,
                }}
              >
                <Tooltip title={t('common.zoomOut', 'Zoom out')}>
                  <IconButton
                    onClick={() => zoomOut()}
                    aria-label={t('common.zoomOut', 'Zoom out')}
                    sx={{ color: 'common.white' }}
                  >
                    <ZoomOutIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common.resetView', 'Reset view')}>
                  <IconButton
                    onClick={() => resetTransform()}
                    aria-label={t('common.resetView', 'Reset view')}
                    sx={{ color: 'common.white' }}
                  >
                    <CenterIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common.zoomIn', 'Zoom in')}>
                  <IconButton
                    onClick={() => zoomIn()}
                    aria-label={t('common.zoomIn', 'Zoom in')}
                    sx={{ color: 'common.white' }}
                  >
                    <ZoomInIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </TransformWrapper>
      </Dialog>
    </>
  );
};

export default PropertyDiagramViewer;
